import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import UmlautHelper from './UmlautHelper';
import UmlautButtons from './UmlautButtons';
import QuestionAudioPlayer from '../shared/QuestionAudioPlayer';

const textareaCls =
  'w-full p-4 pr-[60px] max-md:pr-[50px] max-md:p-3.5 border-2 border-[rgba(18,118,109,0.2)] dark:border-[rgba(20,184,166,0.25)] rounded-xl text-base text-[var(--text-primary)] bg-[var(--card-bg)] dark:bg-[var(--bg-secondary)] resize-y min-h-[100px] transition-all duration-300 leading-relaxed font-[inherit] focus:outline-none focus:border-[#12766d] focus:shadow-[0_0_0_3px_rgba(18,118,109,0.1)] dark:focus:border-[#14b8a6] dark:focus:shadow-[0_0_0_3px_rgba(20,184,166,0.15)] placeholder:opacity-50 placeholder:text-[rgba(26,26,46,0.4)] dark:placeholder:text-[var(--text-tertiary)] max-md:text-base';

const ShortAnswer = ({ question, onAnswer, onNext, sessionData = null, langCode = null }) => {
  const [answer, setAnswer] = useState('');
  const textareaRef = useRef(null);

  // Reset answer when question changes
  useEffect(() => {
    setAnswer('');
  }, [question['soal-id'] || question.id]);

  const handleSubmit = () => {
    if (!answer.trim()) return;

    // For short answer, we don't validate correctness on frontend
    // Backend will handle validation
    if (onAnswer) {
      onAnswer({
        questionId: question['soal-id'] || question.id,
        answer: answer.trim(),
        isCorrect: false // Will be determined by backend
      });
    }
    if (onNext) onNext();
  };

  const questionText = question.text || question['text-soal'] || question.question || '';
  const questionLevelAudioUrl = question?.['audio-url'] || question?.audio_url || null;

  return (
    <motion.div
      className="flex flex-col gap-5 max-md:gap-4 max-md:pb-10"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="flex justify-between items-center pb-4 border-b-2 border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)] max-md:flex-col max-md:items-start max-md:gap-3">
        <motion.span
          className="px-3.5 py-2 bg-gradient-to-br from-[#12766d] to-[#236463] text-white rounded-lg text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap shrink-0 max-md:text-[10px] max-md:px-2 max-md:py-1"
          initial={{ scale: 0.85 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          Short Answer
        </motion.span>
      </div>

      <div className="flex flex-col gap-5">
        {questionLevelAudioUrl && (
          <QuestionAudioPlayer audioUrl={questionLevelAudioUrl} />
        )}
        {questionText && (
          <div className="mb-6 p-5 bg-[rgba(18,118,109,0.05)] dark:bg-[rgba(20,184,166,0.08)] rounded-xl border-l-4 border-[#12766d] dark:border-[#14b8a6] max-md:p-4 max-md:mb-5">
            <p className="text-base leading-relaxed text-[var(--text-primary)] whitespace-pre-line m-0">{questionText}</p>
          </div>
        )}

        <div className="mt-2 max-md:mt-1 relative [&_.umlaut-helper]:absolute [&_.umlaut-helper]:top-3 [&_.umlaut-helper]:right-3 [&_.umlaut-helper]:z-10 max-md:[&_.umlaut-helper]:hidden">
          <UmlautButtons
            inputRef={textareaRef}
            langCode={langCode}
            onInsertChar={(char) => {
              const textarea = textareaRef.current;
              if (textarea) {
                const start = textarea.selectionStart || 0;
                const end = textarea.selectionEnd || 0;
                const newValue = answer.substring(0, start) + char + answer.substring(end);
                setAnswer(newValue);
                setTimeout(() => {
                  textarea.focus();
                  textarea.setSelectionRange(start + char.length, start + char.length);
                }, 0);
              }
            }}
          />
          <textarea
            ref={textareaRef}
            className={textareaCls}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="Write your answer here..."
            rows={4}
          />
          <UmlautHelper langCode={langCode} />
        </div>
      </div>

      <div className="flex justify-center pt-4 border-t-2 border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)]">
        <motion.button
          type="button"
          className="py-3.5 px-7 rounded-xl border-none text-base font-semibold cursor-pointer transition-all duration-300 bg-gradient-to-br from-[#12766d] to-[#0f5d56] text-white shadow-[0_4px_12px_rgba(18,118,109,0.3)] hover:shadow-[0_6px_16px_rgba(18,118,109,0.4)] hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed dark:bg-gradient-to-br dark:from-[#fbbf24] dark:to-[#f59e0b] dark:text-[#1d4746] dark:shadow-[0_4px_14px_rgba(251,191,36,0.35)] dark:hover:from-[#f59e0b] dark:hover:to-[#d97706] dark:hover:shadow-[0_6px_18px_rgba(251,191,36,0.45)] dark:disabled:from-[#334155] dark:disabled:to-[#1e293b] dark:disabled:text-[#64748b] dark:disabled:shadow-none dark:disabled:opacity-70 dark:disabled:hover:transform-none max-md:w-full max-md:py-3.5 max-md:px-6 max-md:min-h-[44px] max-md:text-[15px] touch-manipulation"
          onClick={handleSubmit}
          disabled={!answer.trim()}
          whileTap={{ scale: 0.96 }}
        >
          Submit
        </motion.button>
      </div>
    </motion.div>
  );
};

export default ShortAnswer;

